import type { ImageRuntimeMeta } from '@/ai/image/image-runtime-meta';
import { isPaymentResultToken } from '@/payment/result-token';
import { z } from 'zod';

export const workspaceTaskQuoteRequestSchema = z.object({
  toolId: z.string().min(1),
  toolVersionToken: z.string().min(1),
  originalImageName: z.string().trim().min(1).max(255),
  input: z.record(z.string(), z.unknown()),
  clientImageMeta: z.record(z.string(), z.unknown()).optional(),
  clientRequestId: z.string().min(1).max(200),
  triggerReason: z.enum([
    'credits_insufficient',
    'paid_tool',
    'paid_setting',
    'upload_size_limit',
  ]),
  resumeMode: z.enum(['auto_submit', 'local_file_auto_submit']),
  expectedInputSlots: z
    .array(
      z
        .object({
          fieldId: z.string().min(1),
          itemIndex: z.number().int().nonnegative(),
        })
        .strict()
    )
    .optional(),
  uploadReferences: z.record(z.string(), z.unknown()).optional(),
});

export type WorkspaceTaskQuoteRequestPayload = z.infer<
  typeof workspaceTaskQuoteRequestSchema
>;

const workspaceTaskQuoteSchema = z.object({
  pendingRunId: z.string().startsWith('pr_'),
  quoteId: z.string().startsWith('quote_'),
  clientRequestId: z.string().min(1),
  toolId: z.string().min(1),
  toolVersionToken: z.string().min(1),
  triggerReason: z.enum([
    'credits_insufficient',
    'paid_tool',
    'paid_setting',
    'upload_size_limit',
  ]),
  resumeMode: z.enum(['auto_submit', 'local_file_auto_submit']),
  executionCreditCost: z.number().int().positive(),
  pricingCreditCost: z.number().int().positive(),
  amountCents: z.number().int().nonnegative(),
  taskAmountCents: z.number().int().positive(),
  previouslyPaidAmountCents: z.number().int().nonnegative(),
  priceDifferenceCents: z.number().int().nonnegative(),
  carryForwardAmountCents: z.number().int().nonnegative(),
  paymentMode: z.enum(['new_purchase', 'retry_top_up', 'retry_covered']),
  retrySettingsChanged: z.boolean().optional(),
  retryOriginalSettings: z
    .record(z.string(), z.unknown())
    .nullable()
    .optional(),
  resumePaymentToken: z.string().refine(isPaymentResultToken).optional(),
  currency: z.literal('USD'),
  baseAmountCents: z.number().int().positive(),
  experimentKey: z.string().min(1),
  experimentVariant: z.union([z.literal(0), z.literal(1)]),
  pricingRuleVersion: z.string().min(1),
  paidUploadLimitBytes: z.number().int().positive().nullable(),
  expiresAt: z.string().min(1),
  expectedInputSlots: z.array(
    z.object({
      inputSlotId: z.string().min(1),
      fieldId: z.string().min(1),
      itemIndex: z.number().int().nonnegative(),
    })
  ),
});

export type WorkspaceTaskQuote = z.infer<typeof workspaceTaskQuoteSchema>;

const guestTaskCheckoutRecoverySchema = z.object({
  pendingRunId: z.string().startsWith('pr_'),
  paymentIntentId: z.string().refine(isPaymentResultToken),
  guestRecoveryToken: z.string().min(1).max(256),
});

const taskCheckoutResponseSchema = z.object({
  checkoutId: z.string().min(1),
  checkoutUrl: z.string().url(),
  guestRecovery: guestTaskCheckoutRecoverySchema.optional(),
});

export type GuestTaskCheckoutRecovery = z.infer<
  typeof guestTaskCheckoutRecoverySchema
>;
export type WorkspaceTaskCheckout = z.infer<typeof taskCheckoutResponseSchema>;

const GUEST_TASK_RECOVERY_STORAGE_KEY = 'imgenhancer:guest-task-recovery:v1';
const MAX_STORED_GUEST_TASK_RECOVERIES = 8;

type StoredGuestTaskCheckoutRecovery = GuestTaskCheckoutRecovery & {
  savedAt: number;
};

function readStoredGuestTaskRecoveries(): StoredGuestTaskCheckoutRecovery[] {
  if (typeof window === 'undefined') return [];
  try {
    const raw = window.localStorage.getItem(GUEST_TASK_RECOVERY_STORAGE_KEY);
    if (!raw) return [];
    const parsed = z
      .array(
        guestTaskCheckoutRecoverySchema.extend({
          savedAt: z.number().finite().nonnegative(),
        })
      )
      .safeParse(JSON.parse(raw));
    return parsed.success ? parsed.data : [];
  } catch {
    return [];
  }
}

function writeStoredGuestTaskRecoveries(
  entries: StoredGuestTaskCheckoutRecovery[]
) {
  if (typeof window === 'undefined') return;
  try {
    if (entries.length === 0) {
      window.localStorage.removeItem(GUEST_TASK_RECOVERY_STORAGE_KEY);
      return;
    }
    window.localStorage.setItem(
      GUEST_TASK_RECOVERY_STORAGE_KEY,
      JSON.stringify(entries.slice(0, MAX_STORED_GUEST_TASK_RECOVERIES))
    );
  } catch {
    // Browser storage is best-effort. The signed Stripe callback remains the
    // primary recovery path.
  }
}

export function rememberGuestTaskCheckoutRecovery(
  recovery: GuestTaskCheckoutRecovery
) {
  const normalized = guestTaskCheckoutRecoverySchema.parse(recovery);
  const remaining = readStoredGuestTaskRecoveries().filter(
    (entry) => entry.pendingRunId !== normalized.pendingRunId
  );
  writeStoredGuestTaskRecoveries([
    { ...normalized, savedAt: Date.now() },
    ...remaining,
  ]);
}

export function readLatestGuestTaskCheckoutRecovery(): GuestTaskCheckoutRecovery | null {
  const [latest] = readStoredGuestTaskRecoveries().sort(
    (left, right) => right.savedAt - left.savedAt
  );
  if (!latest) return null;
  return {
    pendingRunId: latest.pendingRunId,
    paymentIntentId: latest.paymentIntentId,
    guestRecoveryToken: latest.guestRecoveryToken,
  };
}

export function forgetGuestTaskCheckoutRecovery(pendingRunId: string) {
  writeStoredGuestTaskRecoveries(
    readStoredGuestTaskRecoveries().filter(
      (entry) => entry.pendingRunId !== pendingRunId
    )
  );
}

export function createWorkspaceTaskClientEventId() {
  return `evt_${crypto.randomUUID().replaceAll('-', '')}`;
}

async function postPaymentApi<TSchema extends z.ZodType>(
  path: string,
  payload: unknown,
  schema: TSchema,
  operationLabel: string
): Promise<z.infer<TSchema>> {
  const response = await fetch(path, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
    signal: AbortSignal.timeout(30_000),
  });
  const body = (await response.json().catch(() => null)) as {
    success?: boolean;
    data?: unknown;
    error?: unknown;
  } | null;
  if (!response.ok || !body?.success) {
    throw new Error(
      typeof body?.error === 'string' && body.error.trim()
        ? body.error
        : `${operationLabel} failed`
    );
  }
  return schema.parse(body.data);
}

export async function requestWorkspaceTaskQuote(
  payload: WorkspaceTaskQuoteRequestPayload
): Promise<WorkspaceTaskQuote> {
  return postPaymentApi(
    '/api/payment/task/quote',
    workspaceTaskQuoteRequestSchema.parse(payload),
    workspaceTaskQuoteSchema,
    'Task quote request'
  );
}

export async function startWorkspaceTaskCheckout(params: {
  pendingRunId: string;
  quoteId: string;
  locale: string;
  callback: string;
}): Promise<WorkspaceTaskCheckout> {
  return postPaymentApi(
    '/api/payment/task/checkout',
    { ...params, taskClickEventId: createWorkspaceTaskClientEventId() },
    taskCheckoutResponseSchema,
    'Task checkout request'
  );
}

export async function recordWorkspaceTaskClientEvent(params: {
  eventType:
    | 'one_time_task_offer_viewed'
    | 'one_time_task_offer_dismissed'
    | 'one_time_task_local_file_staged'
    | 'one_time_task_local_file_stage_failed';
  pendingRunId: string;
  quoteId: string;
}) {
  const response = await fetch('/api/payment/task/lifecycle-event', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      ...params,
      eventId: createWorkspaceTaskClientEventId(),
    }),
    signal: AbortSignal.timeout(15_000),
  });
  if (!response.ok) {
    throw new Error('Task lifecycle event request failed');
  }
}

const workspaceTaskResumeResultSchema = z.object({
  status: z.enum([
    'submitted',
    'already_submitted',
    'needs_local_files',
    'blocked',
  ]),
  ownerType: z.enum(['guest', 'user']).optional(),
  jobId: z.string().min(1).optional(),
  toolId: z.string().min(1).optional(),
  input: z.record(z.string(), z.unknown()).optional(),
  originalImageName: z.string().min(1).optional(),
  errorCode: z.string().min(1).optional(),
  error: z.string().min(1).optional(),
  missingInputSlotIds: z.array(z.string().min(1)).optional(),
  expectedInputSlots: z
    .array(
      z.object({
        inputSlotId: z.string().min(1),
        fieldId: z.string().min(1),
        itemIndex: z.number().int().nonnegative(),
      })
    )
    .optional(),
});

export type WorkspaceTaskResumeResult = z.infer<
  typeof workspaceTaskResumeResultSchema
>;

const resumableWorkspaceTaskPaymentSchema = z.object({
  pendingRunId: z.string().startsWith('pr_'),
  paymentIntentId: z.string().refine(isPaymentResultToken),
  ownerType: z.enum(['guest', 'user']).optional(),
  toolId: z.string().min(1),
  resumeMode: z.enum(['auto_submit', 'local_file_auto_submit']),
});

export type ResumableWorkspaceTaskPayment = z.infer<
  typeof resumableWorkspaceTaskPaymentSchema
>;

export async function findLatestResumableWorkspaceTaskPayment(): Promise<ResumableWorkspaceTaskPayment | null> {
  const response = await fetch('/api/payment/task/pending', {
    headers: { Accept: 'application/json' },
    signal: AbortSignal.timeout(15_000),
  });
  const body = (await response.json().catch(() => null)) as {
    success?: boolean;
    data?: unknown;
  } | null;
  if (!response.ok || !body?.success) {
    throw new Error('Purchased task lookup failed');
  }
  if (body.data === null) return null;
  return resumableWorkspaceTaskPaymentSchema.parse(body.data);
}

export async function resumeWorkspaceTaskPayment(params: {
  pendingRunId: string;
  paymentIntentId: string;
  guestRecoveryToken?: string;
}): Promise<WorkspaceTaskResumeResult> {
  return postPaymentApi(
    '/api/payment/task/resume',
    params,
    workspaceTaskResumeResultSchema,
    'Task resume request'
  );
}

const taskUploadBindResultSchema = z.object({
  status: z.literal('bound'),
  alreadyBound: z.boolean(),
});

export type WorkspaceTaskUploadBind = z.infer<
  typeof taskUploadBindResultSchema
>;

export class WorkspaceTaskUploadBindError extends Error {
  readonly errorCode: string;

  constructor(errorCode: string, message: string) {
    super(message);
    this.name = 'WorkspaceTaskUploadBindError';
    this.errorCode = errorCode;
  }
}

export async function bindWorkspaceTaskUpload(params: {
  pendingRunId: string;
  paymentIntentId: string;
  inputSlotId: string;
  storageUrl: string;
  clientImageMeta: ImageRuntimeMeta;
  guestRecoveryToken?: string;
}): Promise<WorkspaceTaskUploadBind> {
  const response = await fetch('/api/payment/task/upload/bind', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(params),
    signal: AbortSignal.timeout(15_000),
  });
  const body = (await response.json().catch(() => null)) as {
    success?: boolean;
    data?: unknown;
    errorCode?: unknown;
    error?: unknown;
  } | null;
  if (!response.ok || !body?.success) {
    throw new WorkspaceTaskUploadBindError(
      typeof body?.errorCode === 'string' && body.errorCode.trim()
        ? body.errorCode
        : 'TASK_UPLOAD_BIND_FAILED',
      typeof body?.error === 'string' && body.error.trim()
        ? body.error
        : 'Task upload bind failed'
    );
  }
  return taskUploadBindResultSchema.parse(body.data);
}
